import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Leaf, ChevronDown, ChevronRight, QrCode, FileWarning } from 'lucide-react';
import Table, { ActionButton, ActionMenu, StatusBadge } from '../../components/common/Table';
import Modal, { ConfirmModal, FormModal } from '../../components/common/Modal';
import ReportForm from '../../components/forms/ReportForm';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

/**
 * Plants Management Page
 *
 * Comprehensive plant tracking and management interface for administrators.
 * Allows viewing, creating, updating, and deleting individual plant records within beds.
 * Hierarchical structure: Farm → Plot → Bed → Plant
 */
const PlantsPage = () => {
  // Data states
  const [plants, setPlants] = useState([]);
  const [farms, setFarms] = useState([]);
  const [plots, setPlots] = useState([]);
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Hierarchical filter states
  const [selectedFarm, setSelectedFarm] = useState('');
  const [selectedPlot, setSelectedPlot] = useState('');
  const [selectedBed, setSelectedBed] = useState('');

  // Additional filters
  const [filters, setFilters] = useState({
    plant_common_name: '',
    health_status: '',
    growth_stage: '',
    page: 1,
    limit: 20,
  });
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  // Modal states
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [qrCodeGenerating, setQrCodeGenerating] = useState({});

  // Report modal data
  const [reportPlantData, setReportPlantData] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    bed_id: '',
    plant_common_name: '',
    scientific_name: '',
    native_region: '',
    harvest_type: '',
    plant_age_class: '',
    growth_stage: '',
    health_status: '',
    primary_stress_indicator: '',
    damage_source: '',
    damage_location: '',
    field_context: '',
    intervention_status: '',
    market_relevance: '',
    active: true,
  });

  // Load farms on mount
  useEffect(() => {
    loadFarms();
  }, []);

  // Load plots when farm changes
  useEffect(() => {
    if (selectedFarm) {
      loadPlots();
      setSelectedPlot('');
      setSelectedBed('');
    } else {
      setPlots([]);
      setSelectedPlot('');
      setSelectedBed('');
    }
  }, [selectedFarm]);

  // Load beds when plot changes
  useEffect(() => {
    if (selectedPlot) {
      loadBeds();
      setSelectedBed('');
    } else {
      setBeds([]);
      setSelectedBed('');
    }
  }, [selectedPlot]);

  // Load plants when bed changes or filters change
  useEffect(() => {
    if (selectedBed) {
      loadPlants();
    } else {
      setPlants([]);
      setLoading(false);
    }
  }, [selectedBed, filters, sortConfig]);

  const loadFarms = async () => {
    try {
      const response = await apiService.getFarms();
      if (response.data && response.data.farms) {
        setFarms(response.data.farms);
        // Auto-select first farm if available
        if (response.data.farms.length > 0 && !selectedFarm) {
          setSelectedFarm(response.data.farms[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading farms:', error);
      toast.error('Failed to load farms');
    }
  };

  const loadPlots = async () => {
    try {
      const response = await apiService.getPlots({ farm_id: selectedFarm });
      if (response.data && response.data.plots) {
        setPlots(response.data.plots);
        // Auto-select first plot if available
        if (response.data.plots.length > 0 && !selectedPlot) {
          setSelectedPlot(response.data.plots[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading plots:', error);
      toast.error('Failed to load plots');
    }
  };

  const loadBeds = async () => {
    try {
      const response = await apiService.getBeds({ plot_id: selectedPlot });
      if (response.data && response.data.beds) {
        setBeds(response.data.beds);
        // Auto-select first bed if available
        if (response.data.beds.length > 0 && !selectedBed) {
          setSelectedBed(response.data.beds[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading beds:', error);
      toast.error('Failed to load beds');
    }
  };

  const loadPlants = async () => {
    try {
      setLoading(true);
      const params = {
        bed_id: selectedBed,
        ...filters,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };

      const response = await apiService.getPlants(params);
      if (response.data && response.data.plants) {
        setPlants(response.data.plants);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error loading plants:', error);
      toast.error('Failed to load plants');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filter changes
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      bed_id: selectedBed || '',
      plant_common_name: '',
      scientific_name: '',
      native_region: '',
      harvest_type: '',
      plant_age_class: '',
      growth_stage: '',
      health_status: '',
      primary_stress_indicator: '',
      damage_source: '',
      damage_location: '',
      field_context: '',
      intervention_status: '',
      market_relevance: '',
      active: true,
    });
  };

  const handleCreate = () => {
    if (!selectedBed) {
      toast.error('Please select a bed first');
      return;
    }
    resetForm();
    setShowCreateModal(true);
  };

  const handleView = async (plant) => {
    setSelectedPlant(plant);
    setShowViewModal(true);
  };

  const handleEdit = (plant) => {
    setSelectedPlant(plant);
    setFormData({
      bed_id: plant.bed_id,
      plant_common_name: plant.plant_common_name,
      scientific_name: plant.scientific_name || '',
      native_region: plant.native_region || '',
      harvest_type: plant.harvest_type || '',
      plant_age_class: plant.plant_age_class || '',
      growth_stage: plant.growth_stage,
      health_status: plant.health_status,
      primary_stress_indicator: plant.primary_stress_indicator || '',
      damage_source: plant.damage_source || '',
      damage_location: plant.damage_location || '',
      field_context: plant.field_context || '',
      intervention_status: plant.intervention_status || '',
      market_relevance: plant.market_relevance || '',
      active: plant.active,
    });
    setShowEditModal(true);
  };

  const handleDelete = (plant) => {
    setSelectedPlant(plant);
    setShowDeleteModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.plant_common_name || !formData.growth_stage || !formData.health_status) {
      toast.error('Please fill in all required fields');
      return;
    }

    const processedFormData = {
      bed_id: formData.bed_id || selectedBed,
      plant_common_name: formData.plant_common_name.trim(),
      scientific_name: formData.scientific_name?.trim() || '',
      native_region: formData.native_region?.trim() || '',
      harvest_type: formData.harvest_type || '',
      plant_age_class: formData.plant_age_class || '',
      growth_stage: formData.growth_stage,
      health_status: formData.health_status,
      primary_stress_indicator: formData.primary_stress_indicator || '',
      damage_source: formData.damage_source || '',
      damage_location: formData.damage_location || '',
      field_context: formData.field_context || '',
      intervention_status: formData.intervention_status || '',
      market_relevance: formData.market_relevance || '',
      active: formData.active,
    };

    try {
      setFormLoading(true);
      if (showEditModal && selectedPlant) {
        await apiService.updatePlant(selectedPlant.id, processedFormData);
        toast.success('Plant updated successfully');
        setShowEditModal(false);
      } else {
        await apiService.createPlant(processedFormData);
        toast.success('Plant created successfully');
        setShowCreateModal(false);
      }
      loadPlants();
      resetForm();
    } catch (error) {
      console.error('Error saving plant:', error);
      toast.error(error.response?.data?.message || 'Failed to save plant');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiService.deletePlant(selectedPlant.id);
      toast.success('Plant deleted successfully');
      setShowDeleteModal(false);
      loadPlants();
    } catch (error) {
      console.error('Error deleting plant:', error);
      toast.error('Failed to delete plant');
    }
  };

  const handleGenerateQR = async (plant) => {
    try {
      setQrCodeGenerating(prev => ({ ...prev, [plant.id]: true }));
      const response = await apiService.generateQRCode({ plant_id: plant.id });
      toast.success('QR code generated successfully');
      loadPlants(); // Reload to get updated QR code URL
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    } finally {
      setQrCodeGenerating(prev => ({ ...prev, [plant.id]: false }));
    }
  };

  // Report handlers
  const handleReportPlant = async (plant) => {
    try {
      // Fetch full plant details including associated bed, plot, and farm
      const response = await apiService.getPlant(plant.id);
      if (response.success) {
        // Structure the data as expected by ReportForm
        const plantData = {
          plant: response.data.plant,
          bed: response.data.bed,
          plot: response.data.plot,
          farm: response.data.farm,
        };
        setReportPlantData(plantData);
        setShowReportModal(true);
      } else {
        toast.error('Failed to load plant details for reporting');
      }
    } catch (error) {
      console.error('Error loading plant data for report:', error);
      toast.error('Failed to load plant details for reporting');
    }
  };

  const handleReportSuccess = () => {
    setShowReportModal(false);
    setReportPlantData(null);
    toast.success('Report submitted successfully');
  };

  // Table columns configuration
  const columns = [
    {
      key: 'plant_common_name',
      title: 'Plant Name',
      sortable: true,
      width: '150px',
      render: (plant) => (
        <div className="font-medium text-gray-900">
          {plant.plant_common_name}
        </div>
      )
    },
    {
      key: 'scientific_name',
      title: 'Scientific Name',
      sortable: true,
      className: 'hidden md:table-cell',
      render: (plant) => (
        <div className="text-sm text-gray-600 italic">
          {plant.scientific_name || '-'}
        </div>
      )
    },
    {
      key: 'growth_stage',
      title: 'Growth Stage',
      sortable: true,
      render: (plant) => (
        <StatusBadge
          status={plant.growth_stage}
          variant={
            plant.growth_stage === 'flowering' ? 'success' :
            plant.growth_stage === 'vegetative' ? 'primary' :
            'secondary'
          }
        />
      )
    },
    {
      key: 'health_status',
      title: 'Health Status',
      sortable: true,
      render: (plant) => {
        const variant =
          plant.health_status?.includes('healthy') ? 'success' :
          plant.health_status?.includes('mild') ? 'warning' :
          plant.health_status?.includes('moderate') ? 'warning' :
          plant.health_status?.includes('severe') ? 'danger' :
          'secondary';

        return (
          <StatusBadge
            status={plant.health_status?.replace(/_/g, ' ')}
            variant={variant}
          />
        );
      }
    },
    {
      key: 'market_relevance',
      title: 'Market Status',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (plant) => (
        <StatusBadge
          status={plant.market_relevance?.replace(/_/g, ' ')}
          variant={
            plant.market_relevance === 'on_track' ? 'success' :
            plant.market_relevance === 'at_risk' ? 'warning' :
            plant.market_relevance === 'lost' ? 'danger' :
            'secondary'
          }
        />
      )
    },
    {
      key: 'qr_code',
      title: 'QR Code',
      className: 'hidden lg:table-cell',
      render: (plant) => (
        <div>
          {plant.qr_code ? (
            <a
              href={plant.qr_code}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline text-sm"
            >
              View QR
            </a>
          ) : (
            <button
              onClick={() => handleGenerateQR(plant)}
              disabled={qrCodeGenerating[plant.id]}
              className="text-blue-600 hover:text-blue-800 text-sm disabled:opacity-50"
            >
              {qrCodeGenerating[plant.id] ? (
                <span className="flex items-center gap-1">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  Generating...
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <QrCode className="h-4 w-4" />
                  Generate
                </span>
              )}
            </button>
          )}
        </div>
      )
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (plant) => (
        <div className="text-sm text-gray-600">
          {new Date(plant.created_at).toLocaleDateString()}
        </div>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '120px',
      render: (plant) => (
        <ActionMenu
          actions={[
            {
              label: 'View',
              icon: Eye,
              onClick: () => handleView(plant),
            },
            {
              label: 'Edit',
              icon: Edit,
              onClick: () => handleEdit(plant),
            },
            {
              label: 'Submit Report',
              icon: FileWarning,
              onClick: () => handleReportPlant(plant),
            },
            {
              label: 'Delete',
              icon: Trash2,
              onClick: () => handleDelete(plant),
              variant: 'danger',
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Leaf className="h-7 w-7 text-[#025E73]" />
            Plants Management
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage individual plant records within beds
          </p>
        </div>
        <button
          onClick={handleCreate}
          disabled={!selectedBed}
          className="mt-4 sm:mt-0 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Plant
        </button>
      </div>

      {/* Hierarchical Filters */}
      <div className="bg-white shadow-sm rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Farm Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Farm
            </label>
            <select
              value={selectedFarm}
              onChange={(e) => setSelectedFarm(e.target.value)}
              className="form-select w-full"
            >
              <option value="">Select Farm</option>
              {farms.map((farm) => (
                <option key={farm.id} value={farm.id}>
                  {farm.name}
                </option>
              ))}
            </select>
          </div>

          {/* Plot Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plot
            </label>
            <select
              value={selectedPlot}
              onChange={(e) => setSelectedPlot(e.target.value)}
              disabled={!selectedFarm || plots.length === 0}
              className="form-select w-full disabled:opacity-50"
            >
              <option value="">Select Plot</option>
              {plots.map((plot) => (
                <option key={plot.id} value={plot.id}>
                  {plot.name}
                </option>
              ))}
            </select>
          </div>

          {/* Bed Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bed
            </label>
            <select
              value={selectedBed}
              onChange={(e) => setSelectedBed(e.target.value)}
              disabled={!selectedPlot || beds.length === 0}
              className="form-select w-full disabled:opacity-50"
            >
              <option value="">Select Bed</option>
              {beds.map((bed) => (
                <option key={bed.id} value={bed.id}>
                  Bed #{bed.bed_number} - {bed.crop}
                </option>
              ))}
            </select>
          </div>

          {/* Toggle Advanced Filters */}
          <div className="flex items-end">
            <button
              onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
              className="btn btn-secondary w-full"
            >
              {isFiltersCollapsed ? <ChevronRight className="h-4 w-4 mr-2" /> : <ChevronDown className="h-4 w-4 mr-2" />}
              {isFiltersCollapsed ? 'More Filters' : 'Less Filters'}
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        {!isFiltersCollapsed && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plant Name
              </label>
              <input
                type="text"
                value={filters.plant_common_name}
                onChange={(e) => handleFilterChange('plant_common_name', e.target.value)}
                placeholder="Search by name..."
                className="form-input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Health Status
              </label>
              <select
                value={filters.health_status}
                onChange={(e) => handleFilterChange('health_status', e.target.value)}
                className="form-select w-full"
              >
                <option value="">All</option>
                <option value="healthy">Healthy</option>
                <option value="mild_stress">Mild Stress</option>
                <option value="moderate_stress">Moderate Stress</option>
                <option value="severe_stress">Severe Stress</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Growth Stage
              </label>
              <select
                value={filters.growth_stage}
                onChange={(e) => handleFilterChange('growth_stage', e.target.value)}
                className="form-select w-full"
              >
                <option value="">All</option>
                <option value="seedling">Seedling</option>
                <option value="vegetative">Vegetative</option>
                <option value="flowering">Flowering</option>
                <option value="fruiting">Fruiting</option>
                <option value="mature">Mature</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Plants Table */}
      {!selectedBed ? (
        <div className="bg-white shadow-sm rounded-lg p-12 text-center">
          <Leaf className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Bed Selected</h3>
          <p className="text-gray-600">
            Please select a farm, plot, and bed to view plants
          </p>
        </div>
      ) : (
        <Table
          columns={columns}
          data={plants}
          loading={loading}
          sortConfig={sortConfig}
          onSort={handleSort}
          emptyMessage="No plants found. Click 'Add Plant' to create one."
        />
      )}

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Plant Details"
        size="lg"
      >
        {selectedPlant && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Common Name</p>
                <p className="font-medium">{selectedPlant.plant_common_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Scientific Name</p>
                <p className="font-medium italic">{selectedPlant.scientific_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Native Region</p>
                <p className="font-medium">{selectedPlant.native_region || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Harvest Type</p>
                <p className="font-medium">{selectedPlant.harvest_type || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Plant Age Class</p>
                <p className="font-medium">{selectedPlant.plant_age_class || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Growth Stage</p>
                <p className="font-medium">{selectedPlant.growth_stage}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Health Status</p>
                <p className="font-medium">{selectedPlant.health_status}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Primary Stress Indicator</p>
                <p className="font-medium">{selectedPlant.primary_stress_indicator || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Damage Source</p>
                <p className="font-medium">{selectedPlant.damage_source || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Damage Location</p>
                <p className="font-medium">{selectedPlant.damage_location || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Field Context</p>
                <p className="font-medium">{selectedPlant.field_context || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Intervention Status</p>
                <p className="font-medium">{selectedPlant.intervention_status || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Market Relevance</p>
                <p className="font-medium">{selectedPlant.market_relevance || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Created</p>
                <p className="font-medium">{new Date(selectedPlant.created_at).toLocaleString()}</p>
              </div>
            </div>
            {selectedPlant.qr_code && (
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-2">QR Code</p>
                <img src={selectedPlant.qr_code} alt="Plant QR Code" className="w-32 h-32" />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Create/Edit Modal */}
      <FormModal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        onSubmit={handleFormSubmit}
        title={showEditModal ? 'Edit Plant' : 'Create New Plant'}
        submitText={showEditModal ? 'Update' : 'Create'}
        loading={formLoading}
        size="lg"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plant Common Name *
            </label>
            <input
              type="text"
              value={formData.plant_common_name}
              onChange={(e) => handleInputChange('plant_common_name', e.target.value)}
              className="form-input w-full"
              placeholder="e.g., kale"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scientific Name
            </label>
            <input
              type="text"
              value={formData.scientific_name}
              onChange={(e) => handleInputChange('scientific_name', e.target.value)}
              className="form-input w-full"
              placeholder="e.g., Brassica oleracea var. sabellica"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Native Region
            </label>
            <input
              type="text"
              value={formData.native_region}
              onChange={(e) => handleInputChange('native_region', e.target.value)}
              className="form-input w-full"
              placeholder="e.g., Eastern Mediterranean"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Harvest Type
            </label>
            <select
              value={formData.harvest_type}
              onChange={(e) => handleInputChange('harvest_type', e.target.value)}
              className="form-select w-full"
            >
              <option value="">Select...</option>
              <option value="single_cut">Single Cut</option>
              <option value="multiple_cut">Multiple Cut</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plant Age Class
            </label>
            <select
              value={formData.plant_age_class}
              onChange={(e) => handleInputChange('plant_age_class', e.target.value)}
              className="form-select w-full"
            >
              <option value="">Select...</option>
              <option value="new">New</option>
              <option value="mature">Mature</option>
              <option value="old">Old</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Growth Stage *
            </label>
            <select
              value={formData.growth_stage}
              onChange={(e) => handleInputChange('growth_stage', e.target.value)}
              className="form-select w-full"
              required
            >
              <option value="">Select...</option>
              <option value="seedling">Seedling</option>
              <option value="vegetative">Vegetative</option>
              <option value="flowering">Flowering</option>
              <option value="fruiting">Fruiting</option>
              <option value="mature">Mature</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Health Status *
            </label>
            <select
              value={formData.health_status}
              onChange={(e) => handleInputChange('health_status', e.target.value)}
              className="form-select w-full"
              required
            >
              <option value="">Select...</option>
              <option value="healthy">Healthy</option>
              <option value="mild_stress">Mild Stress</option>
              <option value="moderate_stress">Moderate Stress</option>
              <option value="severe_stress">Severe Stress</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Primary Stress Indicator
            </label>
            <input
              type="text"
              value={formData.primary_stress_indicator}
              onChange={(e) => handleInputChange('primary_stress_indicator', e.target.value)}
              className="form-input w-full"
              placeholder="e.g., leaf_holes"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Damage Source
            </label>
            <select
              value={formData.damage_source}
              onChange={(e) => handleInputChange('damage_source', e.target.value)}
              className="form-select w-full"
            >
              <option value="">Select...</option>
              <option value="insect">Insect</option>
              <option value="disease">Disease</option>
              <option value="environmental">Environmental</option>
              <option value="mechanical">Mechanical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Damage Location
            </label>
            <select
              value={formData.damage_location}
              onChange={(e) => handleInputChange('damage_location', e.target.value)}
              className="form-select w-full"
            >
              <option value="">Select...</option>
              <option value="leaves">Leaves</option>
              <option value="stem">Stem</option>
              <option value="roots">Roots</option>
              <option value="flowers">Flowers</option>
              <option value="fruits">Fruits</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Field Context
            </label>
            <select
              value={formData.field_context}
              onChange={(e) => handleInputChange('field_context', e.target.value)}
              className="form-select w-full"
            >
              <option value="">Select...</option>
              <option value="edge">Edge</option>
              <option value="interior">Interior</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Intervention Status
            </label>
            <select
              value={formData.intervention_status}
              onChange={(e) => handleInputChange('intervention_status', e.target.value)}
              className="form-select w-full"
            >
              <option value="">Select...</option>
              <option value="none">None</option>
              <option value="planned">Planned</option>
              <option value="manual_fix">Manual Fix</option>
              <option value="organic_spray">Organic Spray</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Market Relevance
            </label>
            <select
              value={formData.market_relevance}
              onChange={(e) => handleInputChange('market_relevance', e.target.value)}
              className="form-select w-full"
            >
              <option value="">Select...</option>
              <option value="on_track">On Track</option>
              <option value="at_risk">At Risk</option>
              <option value="lost">Lost</option>
            </select>
          </div>

          <div className="flex items-center md:col-span-2">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => handleInputChange('active', e.target.checked)}
              className="h-4 w-4 text-[#025E73] focus:ring-[#025E73] border-gray-300 rounded"
            />
            <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
              Active
            </label>
          </div>
        </div>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Plant"
        message={`Are you sure you want to delete "${selectedPlant?.plant_common_name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Report Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Submit Report"
        size="xl"
      >
        {reportPlantData && (
          <ReportForm
            plantData={reportPlantData}
            onSuccess={handleReportSuccess}
            onCancel={() => setShowReportModal(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default PlantsPage;
